/**
 * @file ShopNavSearch.tsx
 * @description Поиск по меню кабинета магазина (phase19.31, SC16;
 * uiux-design §3.1).
 *
 * Поле закреплено в шапке панели; пока в нём есть текст, вместо групп —
 * плоский список найденных пунктов из ВСЕХ групп (и свёрнутых), с группой,
 * где пункт лежит. Ищет по подписи и синонимам («ТК», «ПВЗ», «блог»), в
 * том числе по разделам страницы «Настройки». Escape очищает поле. Выбор
 * найденного пункта закрывает шторку меню на телефоне (как любой переход).
 */

import { Search } from "lucide-react";

import { searchShopNav, type NavCounters, type VisibleShopNav } from "../../lib/shop-nav";
import { Input } from "../ui/input";
import { NavItemLink } from "./ShopNavGroup";

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
}

/** Поле поиска — закреплено в шапке панели под магазином. */
export function ShopNavSearchField({ value, onChange }: SearchFieldProps) {
  return (
    <div className="relative">
      <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onChange("");
        }}
        placeholder="Найти в меню"
        aria-label="Найти в меню"
        className="pl-9"
      />
    </div>
  );
}

interface ResultsProps {
  nav: VisibleShopNav;
  query: string;
  slug: string;
  counters: NavCounters;
}

/** Найденные пункты вместо групп. */
export function ShopNavSearchResults({ nav, query, slug, counters }: ResultsProps) {
  const hits = searchShopNav(nav, query);
  if (hits.length === 0) {
    return <p className="px-3 py-2 text-sm text-sidebar-muted">Ничего не нашлось</p>;
  }
  return (
    <ul className="flex flex-col gap-0.5" aria-label="Найдено в меню">
      {hits.map(({ item, where }) => (
        <li key={item.id} className="flex flex-col">
          <NavItemLink item={item} slug={slug} counters={counters} />
          {where ? <span className="px-3 pb-1 pl-11 text-xs text-sidebar-muted">{where}</span> : null}
        </li>
      ))}
    </ul>
  );
}
